/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {z} from 'zod';

import {isNotNil} from 'ramda';

import type {FeGridSettings, PresetDisplayMode} from '../types/api/settings';
import type {LocalGridSettings} from './stripLocalGridSettings';

const STORAGE_KEY_PREFIX = 'eag-grid-prefs';

const StoredPrefsSchema = z.object({
  colors: z.record(z.string(), z.string()).optional().catch(undefined),
  pinnedIds: z.array(z.string()).optional().catch(undefined),
  displayMode: z.enum(['TABS_WRAP', 'TABS_SCROLL', 'DROPDOWN', 'NONE']).optional().catch(undefined),
  gridSettings: z
    .object({
      emptyCellValueMode: z.enum(['NOTHING', 'DASH']).optional(),
      truncatedCellHoverMode: z.enum(['TOOLTIP', 'EXPAND', 'NONE']).optional(),
      headerWrapMode: z.enum(['TRUNCATE', 'WRAP']).optional(),
      activeFilterBarVisible: z.boolean().optional(),
      confirmSortReplacement: z.boolean().optional(),
      paginationDisplayMode: z.enum(['BUTTONS', 'INPUT']).optional(),
    })
    .optional()
    .catch(undefined),
});

type StoredPrefs = z.infer<typeof StoredPrefsSchema>;

function storageKey(gridCode: string): string {
  return `${STORAGE_KEY_PREFIX}:${gridCode}`;
}

function readPrefs(gridCode: string): StoredPrefs {
  try {
    const raw = localStorage.getItem(storageKey(gridCode));
    const parsed = StoredPrefsSchema.safeParse(raw ? JSON.parse(raw) : {});
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

function writePrefs(gridCode: string, prefs: StoredPrefs): void {
  try {
    localStorage.setItem(storageKey(gridCode), JSON.stringify(prefs));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

// Legacy colors are read and removed by the one-time backend migration.

export function loadPresetColors(gridCode: string): Map<string, string> {
  const colors = readPrefs(gridCode).colors;
  return colors ? new Map(Object.entries(colors)) : new Map();
}

export function savePresetColors(gridCode: string, colors: Map<string, string>): void {
  const prefs = readPrefs(gridCode);
  prefs.colors = Object.fromEntries(colors);
  writePrefs(gridCode, prefs);
}

// ── Pinned preset IDs ───────────────────────────────────────────────────

export function loadPinnedPresetIds(gridCode: string): Set<string> {
  const ids = readPrefs(gridCode).pinnedIds;
  return ids ? new Set(ids) : new Set();
}

export function savePinnedPresetIds(gridCode: string, ids: Set<string>): void {
  const prefs = readPrefs(gridCode);
  prefs.pinnedIds = [...ids];
  writePrefs(gridCode, prefs);
}

// ── Display mode ────────────────────────────────────────────────────────

export function loadPresetDisplayMode(gridCode: string): PresetDisplayMode | null {
  return readPrefs(gridCode).displayMode ?? null;
}

export function savePresetDisplayMode(gridCode: string, mode: PresetDisplayMode): void {
  const prefs = readPrefs(gridCode);
  prefs.displayMode = mode;
  writePrefs(gridCode, prefs);
}

// ── Grid-scoped display settings ───────────────────────────────────────

export function loadLocalGridSettings(gridCode: string): Partial<LocalGridSettings> {
  return readPrefs(gridCode).gridSettings ?? {};
}

export function saveLocalGridSettings(gridCode: string, settings: Partial<FeGridSettings>): void {
  const changes: Partial<LocalGridSettings> = {
    ...(settings.emptyCellValueMode !== undefined
      ? {emptyCellValueMode: settings.emptyCellValueMode}
      : {}),
    ...(settings.truncatedCellHoverMode !== undefined
      ? {truncatedCellHoverMode: settings.truncatedCellHoverMode}
      : {}),
    ...(settings.headerWrapMode !== undefined ? {headerWrapMode: settings.headerWrapMode} : {}),
    ...(settings.activeFilterBarVisible !== undefined
      ? {activeFilterBarVisible: settings.activeFilterBarVisible}
      : {}),
    ...(isNotNil(settings.confirmSortReplacement)
      ? {confirmSortReplacement: settings.confirmSortReplacement}
      : {}),
    ...(settings.paginationDisplayMode !== undefined
      ? {paginationDisplayMode: settings.paginationDisplayMode}
      : {}),
  };
  if (Object.keys(changes).length === 0) {
    return;
  }
  const prefs = readPrefs(gridCode);
  prefs.gridSettings = {...prefs.gridSettings, ...changes};
  writePrefs(gridCode, prefs);
}
